import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { usersApi, UserCreateData, UserUpdateData } from '../api/users.api';
import { companyApi } from '../api/company.api';
import { User, UserRole, Company } from '../types/user';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../utils/toast';

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  company_id: string;
  phone: string;
  status: 'active' | 'inactive';
}

interface EditUserFormData {
  name: string;
  phone: string;
  role: UserRole;
  status: 'active' | 'inactive';
  password: string;
}

// Role badge colors
const roleColors: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-800',
  company_admin: 'bg-blue-100 text-blue-800',
  driver: 'bg-green-100 text-green-800',
  hr: 'bg-pink-100 text-pink-800',
  accounting: 'bg-yellow-100 text-yellow-800',
  manager: 'bg-indigo-100 text-indigo-800',
};

export const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.isSuperAdmin || false;

  // Derived from translations
  const getRoleLabel = (role: UserRole): string => t(`users.roles.${role}`);

  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');

  const [formData, setFormData] = useState<UserFormData>({
    name: '', email: '', password: '', role: 'driver',
    company_id: '', phone: '', status: 'active',
  });

  const [editFormData, setEditFormData] = useState<EditUserFormData>({
    name: '', phone: '', role: 'driver', status: 'active', password: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});

  const getAllowedRoles = (): UserRole[] => {
    if (isSuperAdmin) {
      return ['super_admin', 'company_admin', 'driver', 'hr', 'accounting', 'manager'];
    }
    return ['company_admin', 'driver', 'hr', 'accounting', 'manager'];
  };

  const allowedRoles = getAllowedRoles();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const usersData = await usersApi.listUsers();
      setUsers(Array.isArray(usersData) ? usersData : []);

      if (isSuperAdmin) {
        try {
          const companiesResponse = await companyApi.listCompanies();
          setCompanies(companiesResponse.data || []);
        } catch (error) {
          console.error('Failed to load companies:', error);
          setCompanies([]);
        }
      }
    } catch (error: any) {
      console.error('Failed to load data:', error);
      toast.error(error.response?.data?.message || t('users.failedLoad'));
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) errors.name = t('users.validation.nameRequired');
    if (!formData.email.trim()) {
      errors.email = t('users.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('users.validation.emailInvalid');
    }
    if (!formData.password) {
      errors.password = t('users.validation.passwordRequired');
    } else if (formData.password.length < 8) {
      errors.password = t('users.validation.passwordMinLength');
    }
    if (!formData.role) errors.role = t('users.validation.roleRequired');
    if (isSuperAdmin && formData.role !== 'super_admin' && !formData.company_id) errors.company_id = t('users.validation.companyRequired');
    if (formData.phone && !/^\+?[0-9]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = t('users.validation.phoneInvalid');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const createData: UserCreateData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role,
        phone: formData.phone.trim() || undefined,
        status: formData.status,
      };

      if (isSuperAdmin && formData.company_id) {
        createData.company_id = formData.company_id;
      }

      await usersApi.createUser(createData);

      setCreatedCredentials({ email: createData.email, password: formData.password });
      setIsAddModalOpen(false);
      setShowSuccessModal(true);

      setFormData({ name: '', email: '', password: '', role: 'driver', company_id: '', phone: '', status: 'active' });
      setFormErrors({});

      await loadData();
    } catch (error: any) {
      console.error('Failed to add user:', error);
      toast.error(error.response?.data?.message || t('users.addModal.failedAdd'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateEditForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!editFormData.name.trim()) errors.name = t('users.validation.nameRequired');
    if (!editFormData.role) errors.role = t('users.validation.roleRequired');
    if (editFormData.password && editFormData.password.length < 8) {
      errors.password = t('users.validation.passwordMinLength');
    }
    if (editFormData.phone && !/^\+?[0-9]{10,15}$/.test(editFormData.phone.replace(/\s/g, ''))) {
      errors.phone = t('users.validation.phoneInvalid');
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditFormData({ name: user.name, phone: user.phone || '', role: user.role, status: user.status, password: '' });
    setEditFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleEditInputChange = (field: keyof EditUserFormData, value: string) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    if (editFormErrors[field]) {
      setEditFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    if (!editingUser) return;

    try {
      setIsSubmitting(true);

      const updateData: UserUpdateData = {
        name: editFormData.name.trim(),
        phone: editFormData.phone.trim() || undefined,
        role: editFormData.role,
        status: editFormData.status,
      };

      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      await usersApi.updateUser(editingUser._id, updateData);
      toast.success(t('users.editModal.updatedSuccess'));

      setIsEditModalOpen(false);
      setEditingUser(null);
      setEditFormData({ name: '', phone: '', role: 'driver', status: 'active', password: '' });
      setEditFormErrors({});

      await loadData();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || t('users.editModal.failedUpdate'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = (user: User) => {
    setDeletingUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;

    try {
      setIsSubmitting(true);
      await usersApi.deleteUser(deletingUser._id);
      toast.success(t('users.deleteModal.deletedSuccess'));

      setIsDeleteModalOpen(false);
      setDeletingUser(null);

      await loadData();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || t('users.deleteModal.failedDelete'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCredentials = () => {
    if (createdCredentials) {
      const text = `Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`;
      navigator.clipboard.writeText(text);
      toast.success(t('users.successModal.copiedToClipboard'));
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !searchQuery ||
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.phone && user.phone.includes(searchQuery));
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesStatus = !statusFilter || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">{t('users.subtitle')}</p>
          </div>
          <Button onClick={() => setIsAddModalOpen(true)} size="lg">
            {t('users.addUser')}
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Input
                type="search"
                placeholder={t('users.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
              >
                <option value="">{t('users.allRoles')}</option>
                {allowedRoles.map((role) => (
                  <option key={role} value={role}>
                    {getRoleLabel(role)}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | '')}
              >
                <option value="">{t('users.allStatuses')}</option>
                <option value="active">{t('users.table.active')}</option>
                <option value="inactive">{t('users.table.inactive')}</option>
              </Select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
              <p className="mt-4 text-gray-600">{t('users.loading')}</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery || roleFilter || statusFilter
                  ? t('users.noUsersFound')
                  : t('users.noUsersYet')}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || roleFilter || statusFilter
                  ? t('users.tryFilters')
                  : t('users.getStarted')}
              </p>
              {!searchQuery && !roleFilter && !statusFilter && (
                <Button onClick={() => setIsAddModalOpen(true)}>
                  {t('users.addFirstUser')}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.role')}
                    </th>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('users.table.company')}
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.created')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user._id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/users/${user._id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 font-medium text-sm">
                              {user.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            {user.phone && (
                              <div className="text-sm text-gray-500">{user.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${roleColors[user.role]}`}
                        >
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(user as any).company_id?.name || user.company?.name || '—'}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.status === 'active'
                            ? t('users.table.active')
                            : t('users.table.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditUser(user); }}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                            title={t('users.table.editTitle')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title={t('users.table.deleteTitle')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        {!isLoading && users.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('users.stats.total')}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{users.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('users.stats.active')}</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {users.filter((u) => u.status === 'active').length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('users.stats.admins')}</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {users.filter((u) =>
                  u.role === 'admin' || u.role === 'company_admin' || u.role === 'super_admin'
                ).length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('users.stats.drivers')}</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {users.filter((u) => u.role === 'driver').length}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => !isSubmitting && setIsAddModalOpen(false)}
        title={t('users.addModal.title')}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('users.addModal.fullName')}
            placeholder={t('users.addModal.namePlaceholder')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={formErrors.name}
            disabled={isSubmitting}
            autoFocus
          />
          <Input
            label={t('users.addModal.email')}
            type="email"
            placeholder={t('users.addModal.emailPlaceholder')}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={formErrors.email}
            disabled={isSubmitting}
          />
          <Input
            label={t('users.addModal.password')}
            type="password"
            placeholder={t('users.addModal.passwordPlaceholder')}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={formErrors.password}
            helperText={t('users.addModal.passwordHelper')}
            disabled={isSubmitting}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.addModal.role')}
            </label>
            <Select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              disabled={isSubmitting}
              error={formErrors.role}
            >
              {allowedRoles.map((role) => (
                <option key={role} value={role}>
                  {getRoleLabel(role)}
                </option>
              ))}
            </Select>
            {formErrors.role && (
              <p className="mt-1 text-sm text-red-600">{formErrors.role}</p>
            )}
          </div>

          {isSuperAdmin && formData.role !== 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.addModal.company')}
              </label>
              <Select
                value={formData.company_id}
                onChange={(e) => handleInputChange('company_id', e.target.value)}
                disabled={isSubmitting}
                error={formErrors.company_id}
              >
                <option value="">{t('users.addModal.selectCompany')}</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </Select>
              {formErrors.company_id && (
                <p className="mt-1 text-sm text-red-600">{formErrors.company_id}</p>
              )}
            </div>
          )}

          <Input
            label={t('users.addModal.phone')}
            type="tel"
            placeholder={t('users.addModal.phonePlaceholder')}
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={formErrors.phone}
            disabled={isSubmitting}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.addModal.statusLabel')}
            </label>
            <Select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              disabled={isSubmitting}
            >
              <option value="active">{t('users.addModal.statusActive')}</option>
              <option value="inactive">{t('users.addModal.statusInactive')}</option>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsAddModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('users.addModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {isSubmitting ? t('users.addModal.addingUser') : t('users.addModal.addUser')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setCreatedCredentials(null);
        }}
        title={t('users.successModal.title')}
        size="md"
        showCloseButton={false}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900">
              {t('users.successModal.accountCreated')}
            </p>
          </div>

          {createdCredentials && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
              <p className="text-sm text-gray-600 mb-3 font-medium">
                {t('users.successModal.loginCredentials')}
              </p>
              <div className="space-y-2 text-left">
                <div className="flex items-center justify-between bg-white px-3 py-2 rounded">
                  <span className="text-xs text-gray-500">
                    {t('users.successModal.emailLabel')}
                  </span>
                  <span className="font-mono text-sm text-gray-900">
                    {createdCredentials.email}
                  </span>
                </div>
                <div className="flex items-center justify-between bg-white px-3 py-2 rounded">
                  <span className="text-xs text-gray-500">
                    {t('users.successModal.passwordLabel')}
                  </span>
                  <span className="font-mono text-sm text-gray-900">
                    {createdCredentials.password}
                  </span>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-600 mb-4">{t('users.successModal.saveCredentials')}</p>

          <div className="flex flex-col gap-2">
            <Button onClick={copyCredentials} variant="secondary">
              {t('users.successModal.copyCredentials')}
            </Button>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                setCreatedCredentials(null);
              }}
            >
              {t('users.successModal.done')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isSubmitting && setIsEditModalOpen(false)}
        title={t('users.editModal.title')}
        size="md"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label={t('users.editModal.fullName')}
            placeholder={t('users.editModal.namePlaceholder')}
            value={editFormData.name}
            onChange={(e) => handleEditInputChange('name', e.target.value)}
            error={editFormErrors.name}
            disabled={isSubmitting}
            autoFocus
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.editModal.email')}
            </label>
            <input
              type="email"
              value={editingUser?.email || ''}
              disabled={true}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
            />
            <p className="mt-1 text-sm text-gray-500">{t('users.editModal.emailCannotChange')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.editModal.role')}
            </label>
            <Select
              value={editFormData.role}
              onChange={(e) => handleEditInputChange('role', e.target.value)}
              disabled={isSubmitting}
              error={editFormErrors.role}
            >
              {allowedRoles.map((role) => (
                <option key={role} value={role}>
                  {getRoleLabel(role)}
                </option>
              ))}
            </Select>
            {editFormErrors.role && (
              <p className="mt-1 text-sm text-red-600">{editFormErrors.role}</p>
            )}
          </div>

          <Input
            label={t('users.editModal.phone')}
            type="tel"
            placeholder={t('users.editModal.phonePlaceholder')}
            value={editFormData.phone}
            onChange={(e) => handleEditInputChange('phone', e.target.value)}
            error={editFormErrors.phone}
            disabled={isSubmitting}
          />

          <Input
            label={t('users.editModal.newPassword')}
            type="password"
            placeholder={t('users.editModal.passwordPlaceholder')}
            value={editFormData.password}
            onChange={(e) => handleEditInputChange('password', e.target.value)}
            error={editFormErrors.password}
            helperText={t('users.editModal.passwordHelper')}
            disabled={isSubmitting}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.editModal.statusLabel')}
            </label>
            <Select
              value={editFormData.status}
              onChange={(e) => handleEditInputChange('status', e.target.value)}
              disabled={isSubmitting}
            >
              <option value="active">{t('users.editModal.statusActive')}</option>
              <option value="inactive">{t('users.editModal.statusInactive')}</option>
            </Select>
            <p className="mt-1 text-sm text-gray-500">{t('users.editModal.statusHelper')}</p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('users.editModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {isSubmitting ? t('users.editModal.updatingUser') : t('users.editModal.updateUser')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => !isSubmitting && setIsDeleteModalOpen(false)}
        title={t('users.deleteModal.title')}
        size="sm"
      >
        <div className="space-y-4">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('users.deleteModal.confirmation')}
            </h3>
            {deletingUser && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-gray-900">{deletingUser.name}</p>
                <p className="text-sm text-gray-500">{deletingUser.email}</p>
              </div>
            )}
            <p className="text-sm text-gray-600">{t('users.deleteModal.consequence')}</p>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('users.deleteModal.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              isLoading={isSubmitting}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? t('users.deleteModal.deleting') : t('users.deleteModal.deleteUser')}
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};
